/**
 * Syncs gift/delivery form values to cart attributes so checkout can prefill address fields.
 */
window.DeliveryCheckoutSync = {
  /** @returns {string} */
  getUpdateUrl() {
    if (window.Theme?.routes?.cart_update_url) {
      return window.Theme.routes.cart_update_url.endsWith('.js')
        ? window.Theme.routes.cart_update_url
        : `${window.Theme.routes.cart_update_url}.js`;
    }
    return '/cart/update.js';
  },

  /**
   * @param {string} fullName
   * @returns {{ firstName: string, lastName: string }}
   */
  splitName(fullName) {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return { firstName: '', lastName: '' };
    }
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '.' };
    }
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' '),
    };
  },

  /**
   * @param {Record<string, string>} attributes
   */
  async updateCartAttributes(attributes) {
    const filtered = Object.fromEntries(
      Object.entries(attributes).filter(([, value]) => typeof value === 'string' && value.trim())
    );

    if (Object.keys(filtered).length === 0) {
      return;
    }

    await fetch(this.getUpdateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes: filtered }),
    });
  },

  /**
   * @param {Record<string, string>} attributes
   */
  enrichShippingAttributes(attributes) {
    const senderName = attributes['Gönderen Adı'] || '';
    const senderPhone = attributes['Gönderen Telefon'] || '';
    const recipientName = attributes['Alıcı Adı'] || '';
    const recipientPhone = attributes['Alıcı Telefon'] || '';
    const address = attributes['Teslimat Adresi'] || '';
    const district = attributes['İlçe'] || '';
    const location = attributes['Gönderim Bölgesi'] || '';
    const deliveryDay = attributes['Teslimat Günü'] || '';
    const deliveryTime = attributes['Teslimat Saati'] || '';
    const cardNote = attributes['Kart Notu'] || '';
    const { firstName, lastName } = this.splitName(recipientName);
    const areaLine = [district, location].filter(Boolean).join(' · ');
    const addressLine = [address, areaLine].filter(Boolean).join(' — ');

    if (senderName) attributes['Gönderen Adı'] = senderName;
    if (senderPhone) attributes['Gönderen Telefon'] = senderPhone;
    if (deliveryDay) attributes['Teslimat Günü'] = deliveryDay;
    if (deliveryTime) attributes['Teslimat Saati'] = deliveryTime;
    if (cardNote) attributes['Kart Notu'] = cardNote;

    attributes._shipping_first_name = firstName;
    attributes._shipping_last_name = lastName;
    attributes._shipping_phone = recipientPhone;
    attributes._shipping_address1 = addressLine || areaLine;
    attributes._shipping_address2 = areaLine;
    attributes._shipping_city = 'Kayseri';
    attributes._shipping_province = 'TR38';
    attributes._shipping_country = 'TR';
    attributes._shipping_zip = '38000';

    attributes.shipping_first_name = firstName;
    attributes.shipping_last_name = lastName;
    attributes.shipping_phone = recipientPhone;
    attributes.shipping_address1 = addressLine || areaLine;
    attributes.shipping_address2 = areaLine;
    attributes.shipping_city = 'Kayseri';
    attributes.shipping_province = 'TR38';
    attributes.shipping_country = 'TR';
    attributes.shipping_zip = '38000';

    return attributes;
  },

  /**
   * @param {string} formId
   * @returns {Record<string, string>}
   */
  collectAttributesFromForm(formId) {
    /** @type {Record<string, string>} */
    const attributes = {};
    const form = formId ? document.getElementById(formId) : null;

    /** @type {HTMLElement[]} */
    const scopes = [];
    if (form instanceof HTMLFormElement) scopes.push(form);

    const productScope = form?.closest('.product-information, .product-details, [data-testid="product-information-details"]');
    if (productScope instanceof HTMLElement) scopes.push(productScope);

    if (formId) {
      const giftForm = document.querySelector(`gift-recipient-form[data-form-id="${formId}"]`);
      const deliveryScheduler = document.querySelector(`delivery-scheduler[data-form-id="${formId}"]`);
      if (giftForm instanceof HTMLElement) scopes.push(giftForm);
      if (deliveryScheduler instanceof HTMLElement) scopes.push(deliveryScheduler);
    }

    /** @type {Set<Element>} */
    const seenFields = new Set();

    scopes.forEach((scope) => {
      scope.querySelectorAll('[name^="properties["]').forEach((field) => {
        if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
        if (form instanceof HTMLFormElement) {
          const associated = field.form === form || field.getAttribute('form') === form.id;
          if (!associated) return;
        }
        if (seenFields.has(field)) return;
        seenFields.add(field);

        if (field instanceof HTMLInputElement && field.type === 'checkbox') {
          if (!field.checked) return;

          const match = field.name.match(/^properties\[(.+)\]$/);
          if (match?.[1]) {
            attributes[match[1]] = field.value.trim() || 'Evet';
          }
          return;
        }

        if (!field.value.trim()) return;

        const match = field.name.match(/^properties\[(.+)\]$/);
        if (match?.[1]) {
          attributes[match[1]] = field.value.trim();
        }
      });
    });

    return this.enrichShippingAttributes(attributes);
  },

  /** @param {string} formId */
  async syncFromProductForm(formId) {
    if (!formId) return;
    await this.updateCartAttributes(this.collectAttributesFromForm(formId));
  },

  async syncFromCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;

      const cart = await response.json();
      /** @type {Record<string, string>} */
      const attributes = {};

      cart.items?.forEach((item) => {
        if (!item.properties) return;

        Object.entries(item.properties).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim() && !attributes[key]) {
            attributes[key] = value.trim();
          }
        });
      });

      if (cart.attributes && typeof cart.attributes === 'object') {
        Object.entries(cart.attributes).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim() && !attributes[key]) {
            attributes[key] = value.trim();
          }
        });
      }

      if (Object.keys(attributes).length === 0) {
        return;
      }

      await this.updateCartAttributes(this.enrichShippingAttributes(attributes));
    } catch (_error) {
      // optional sync
    }
  },
};

document.addEventListener(
  'submit',
  async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const submitter = event.submitter;
    const isCheckoutSubmit = submitter instanceof HTMLButtonElement && submitter.name === 'checkout';

    if (!isCheckoutSubmit) return;

    if (form.dataset.deliveryCheckoutSynced === 'true') {
      form.removeAttribute('data-delivery-checkout-synced');
      return;
    }

    event.preventDefault();

    try {
      await window.DeliveryCheckoutSync.syncFromCart();
    } catch (_error) {
      // Checkout should still continue if sync fails.
    }

    form.dataset.deliveryCheckoutSynced = 'true';
    form.requestSubmit(submitter);
  },
  true
);

document.addEventListener(
  'click',
  async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const checkoutLink = target.closest('a[href*="/checkout"]');
    if (!checkoutLink || !(checkoutLink instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    await window.DeliveryCheckoutSync.syncFromCart();
    window.location.href = checkoutLink.href;
  },
  true
);
